import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Upload, Play, Ban, RefreshCw, Activity, Clock, ChevronDown,
  ChevronUp, ArrowLeft, ListChecks,
} from 'lucide-react';
import DropZone from '../components/bank-import/DropZone';
import ImportQueue from '../components/bank-import/ImportQueue';
import FilePreviewDrawer from '../components/bank-import/FilePreviewDrawer';
import DuplicateResolver from '../components/bank-import/DuplicateResolver';

/**
 * Universal Import Manager (Wave 2-C-2).
 *
 * One-stop workspace to ingest any number of bank statements (CSV,
 * Excel, PDF, or ZIPs containing them). Backed by the Import Session
 * Aggregate Root — the page polls /sessions/{id} every 1.2s for live
 * progress and reflects the file state machine in real time.
 */

const POLL_MS = 1200;


function KPIBar({ summary, status, durationMs }) {
  const s = summary || {};
  const items = [
    { label: 'إجمالي الملَفات',  value: s.files_total      ?? 0, tone: 'bg-slate-50 border-slate-200 text-slate-800' },
    { label: 'مُستَورَدَة',         value: s.files_imported   ?? 0, tone: 'bg-emerald-50 border-emerald-200 text-emerald-800' },
    { label: 'مُكَرَّرَة',          value: s.files_duplicated ?? 0, tone: 'bg-amber-50 border-amber-200 text-amber-800' },
    { label: 'فاشِلَة',           value: s.files_failed     ?? 0, tone: 'bg-red-50 border-red-200 text-red-800' },
    { label: 'السُّطور',           value: s.lines_total      ?? 0, tone: 'bg-indigo-50 border-indigo-200 text-indigo-800' },
    { label: 'مُطابَقَة',          value: s.lines_matched    ?? 0, tone: 'bg-emerald-50 border-emerald-200 text-emerald-800' },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="session-kpis">
      {items.map((it) => (
        <div key={it.label} className={`rounded-xl border p-3 ${it.tone}`}>
          <div className="text-[10px] uppercase font-semibold opacity-75">{it.label}</div>
          <div className="text-2xl font-bold font-mono">{it.value}</div>
        </div>
      ))}
      <div className="rounded-xl border p-3 bg-white border-slate-200 col-span-2 md:col-span-6 flex items-center justify-between text-xs">
        <div className="flex items-center gap-2 text-slate-600">
          <Activity className="w-4 h-4 text-indigo-500" />
          <span data-testid="session-status">الحالَة: <b>{status || '—'}</b></span>
        </div>
        {durationMs != null && (
          <div className="flex items-center gap-1.5 text-slate-500 font-mono">
            <Clock className="w-3.5 h-3.5" /> {(durationMs / 1000).toFixed(1)} ث
          </div>
        )}
        {Number.isFinite(s.avg_health) && s.avg_health > 0 && (
          <div className="text-slate-500">
            مُتَوَسِّط الصِّحَّة: <b className="font-mono">{(s.avg_health * 100).toFixed(0)}%</b>
          </div>
        )}
      </div>
    </div>
  );
}


function RecentSessions({ sessions, onPick }) {
  if (!sessions.length) return null;
  return (
    <div className="rounded-xl border bg-white p-3" data-testid="recent-sessions">
      <div className="flex items-center gap-2 mb-2">
        <ListChecks className="w-4 h-4 text-slate-500" />
        <div className="text-sm font-semibold">آخِر الجَلَسات</div>
      </div>
      <div className="space-y-1.5 max-h-72 overflow-y-auto">
        {sessions.map((s) => (
          <button key={s.id} onClick={() => onPick(s.id)}
                  data-testid={`recent-${s.id}`}
                  className="w-full text-start flex items-center justify-between rounded-lg border border-slate-200 hover:bg-slate-50 px-3 py-2 text-xs">
            <div>
              <div className="font-mono text-slate-700">{s.id.slice(0, 8)}…</div>
              <div className="text-[10px] text-slate-500">{s.started_at?.slice(0, 16)?.replace('T', ' ')}</div>
            </div>
            <div className="text-end">
              <div className={`inline-block text-[10px] px-2 py-0.5 rounded-full border ${
                s.status === 'completed' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' :
                s.status === 'running'   ? 'bg-indigo-50 border-indigo-200 text-indigo-700' :
                s.status === 'partial'   ? 'bg-amber-50 border-amber-200 text-amber-700' :
                s.status === 'cancelled' ? 'bg-zinc-50 border-zinc-200 text-zinc-700' :
                                           'bg-red-50 border-red-200 text-red-700'}`}>{s.status}</div>
              <div className="text-[10px] text-slate-400 mt-0.5">
                {(s.summary?.files_total ?? 0)} ملَف
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}


export default function BankImportManager() {
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState('');
  const [files, setFiles] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [session, setSession] = useState(null);   // GET /sessions/{id}
  const [recent, setRecent] = useState([]);
  const [showUploader, setShowUploader] = useState(true);
  const [previewIdx, setPreviewIdx] = useState(null);
  const [resolveIdx, setResolveIdx] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  // ── Bootstrap accounts + recent sessions ──────────────────────
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosInstance.get('/bank/accounts');
        const list = Array.isArray(data) ? data : (data?.items || []);
        setAccounts(list);
        if (list.length && !accountId) setAccountId(list[0].id);
      } catch { /* silent */ }
      try {
        const { data } = await axiosInstance.get('/bank-import/sessions',
          { params: { limit: 10 } });
        setRecent(data.items || []);
      } catch { /* silent */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Polling loop while a session is running ───────────────────
  const poll = useCallback(async (sid) => {
    try {
      const { data } = await axiosInstance.get(`/bank-import/sessions/${sid}`);
      setSession(data);
      if (data.status && data.status !== 'running') {
        clearInterval(pollRef.current);
        pollRef.current = null;
        // Refresh recent list when done
        try {
          const r = await axiosInstance.get('/bank-import/sessions',
            { params: { limit: 10 } });
          setRecent(r.data.items || []);
        } catch { /* silent */ }
      }
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    poll(sessionId);
    pollRef.current = setInterval(() => poll(sessionId), POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [sessionId, poll]);

  // ── Actions ────────────────────────────────────────────────────
  const startImport = async () => {
    if (!accountId) { toast.error('اِختَر حِساباً بَنكِياً'); return; }
    if (!files.length) { toast.error('أَضِف ملَفاً واحِداً على الأَقَل'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('bank_account_id', accountId);
      for (const f of files) fd.append('files', f);
      const { data } = await axiosInstance.post(
        '/bank-import/sessions', fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setSessionId(data.id);
      setSession(data);
      setShowUploader(false);
      setFiles([]);
      toast.success(`بَدَأت جَلسَة ${data.id.slice(0, 8)}…`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فَشَل بَدء الاستيراد');
    } finally { setBusy(false); }
  };

  const cancelSession = async () => {
    if (!sessionId) return;
    try {
      await axiosInstance.post(`/bank-import/sessions/${sessionId}/cancel`);
      toast.message('تَم طَلَب الإلغاء — سيَتَوَقَّف بَعد إنهاء الملَف الجاري.');
      poll(sessionId);
    } catch { toast.error('فَشَل الإلغاء'); }
  };

  const replaySession = async () => {
    if (!sessionId) return;
    try {
      await axiosInstance.post(`/bank-import/sessions/${sessionId}/replay`, {});
      toast.success('تَم إعادَة تَشغيل الجَلسَة');
      poll(sessionId);
    } catch { toast.error('فَشَل إعادَة التَّشغيل'); }
  };

  const retryFile = async (idx) => {
    if (!sessionId) return;
    try {
      await axiosInstance.post(`/bank-import/sessions/${sessionId}/files/${idx}/retry`);
      toast.success(`تَم إعادَة محاوَلَة الملَف رَقم ${idx + 1}`);
      poll(sessionId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فَشَل إعادَة المحاوَلَة');
    }
  };

  const onPickRecent = (sid) => {
    setSessionId(sid);
    setShowUploader(false);
  };

  const sessionFiles = session?.files || [];
  const fileMeta = previewIdx != null ? sessionFiles[previewIdx]
                  : resolveIdx != null ? sessionFiles[resolveIdx] : null;

  return (
    <div className="p-4 max-w-7xl mx-auto" dir="rtl">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Upload className="w-6 h-6 text-indigo-600" />
            مُدير استيراد البُنوك
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            رَفع شامِل — Drag &amp; Drop ومُجَلَّدات وZIP — مَع طابور حَيّ ومُعايَنَة وحَلّ التَّكرار.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/bank-statements-registry" data-testid="open-registry-link"
             className="inline-flex items-center gap-1.5 rounded-lg bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 text-sm font-semibold px-3 py-2">
            <ArrowLeft className="w-4 h-4" /> سِجِل الكُشوف
          </a>
          <button onClick={() => setShowUploader((s) => !s)}
                  data-testid="toggle-uploader"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 text-sm font-semibold px-3 py-2">
            {showUploader ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {showUploader ? 'إخفاء الرَّفع' : 'إظهار الرَّفع'}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr,300px] gap-4">
        <div className="space-y-4">
          {/* Uploader card */}
          {showUploader && (
            <div className="rounded-2xl border bg-white p-4 space-y-3" data-testid="uploader-card">
              <div className="flex items-center gap-3 flex-wrap">
                <label className="text-sm font-semibold text-slate-700">الحِساب البَنكي</label>
                <select value={accountId} onChange={(e) => setAccountId(e.target.value)}
                        data-testid="account-select"
                        className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm min-w-[260px] bg-white">
                  <option value="">— اختَر حِساباً —</option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.bank_name || a.account_name} — {a.account_number || a.iban}
                    </option>
                  ))}
                </select>
                <div className="flex-1" />
                <button onClick={startImport} disabled={busy || !accountId || !files.length}
                        data-testid="start-import-btn"
                        className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 disabled:opacity-50">
                  <Play className="w-4 h-4" /> بَدء الاستيراد
                </button>
              </div>
              <DropZone value={files} onChange={setFiles} disabled={busy} />
            </div>
          )}

          {/* Session monitor */}
          {sessionId && session && (
            <div className="space-y-3" data-testid="session-monitor">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-sm">
                  <span className="text-slate-500">جَلسَة:</span>
                  <span className="font-mono mr-1 text-slate-700">{sessionId}</span>
                </div>
                <div className="flex items-center gap-2">
                  {session.status === 'running' && (
                    <button onClick={cancelSession} data-testid="cancel-session-btn"
                            className="inline-flex items-center gap-1 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-3 py-1.5">
                      <Ban className="w-3.5 h-3.5" /> إلغاء
                    </button>
                  )}
                  {session.status !== 'running' && (
                    <button onClick={replaySession} data-testid="replay-session-btn"
                            className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-3 py-1.5">
                      <RefreshCw className="w-3.5 h-3.5" /> إعادَة تَشغيل الكُل
                    </button>
                  )}
                </div>
              </div>
              <KPIBar summary={session.summary} status={session.status} durationMs={session.duration_ms} />
              <ImportQueue
                files={sessionFiles}
                sessionStatus={session.status}
                onPreview={setPreviewIdx}
                onRetry={retryFile}
                onResolve={setResolveIdx}
              />
            </div>
          )}
        </div>

        <div className="space-y-3">
          <RecentSessions sessions={recent} onPick={onPickRecent} />
        </div>
      </div>

      {previewIdx != null && fileMeta && (
        <FilePreviewDrawer
          sessionId={sessionId}
          fileIndex={previewIdx}
          fileMeta={fileMeta}
          onClose={() => setPreviewIdx(null)}
        />
      )}
      {resolveIdx != null && fileMeta && (
        <DuplicateResolver
          sessionId={sessionId}
          fileIndex={resolveIdx}
          fileMeta={fileMeta}
          onClose={() => setResolveIdx(null)}
          onResolved={() => poll(sessionId)}
        />
      )}
    </div>
  );
}
